/**  Tag Group URL Encoding

    Tags are stored in the URL via the `tags` query parameter as a comma-separated list.
    e.g. ?tags=MD%2B1,Training,Imported

    The same tag name can exist in multiple groups (Day Code, Activity, Data Sharing).
    e.g. a tag called "Recovery" could be both a Day Code tag and an Activity tag.

    So, when a tag name appears in more than one group, we disambiguate it in the URL
    by appending a tilde and a short group key: "name~groupKey".
    e.g. ?tags=Recovery~dc,Recovery~act,Training

    Group keys:
    dc  = Day Code       (API type: 'DayCode')
    act = Activity        (API type: 'activity')
    ds  = Data Sharing    (API type: 'Data Sharing')

    Tags with unique names across all groups are stored as plain names (no suffix).
    When sending tags to the API, suffixes are stripped via stripTagGroupSuffix() since
    the API filters by name only, not by group.

    Flow: UI selection → buildTagFilterKey() → URL → parseTagFilterKey() → UI restoration
        URL → stripTagGroupSuffix() → API query
*/

export type TagGroupKey = 'dc' | 'act' | 'ds';
export const TAG_GROUP_KEY_DAY_CODE: TagGroupKey = 'dc';
export const TAG_GROUP_KEY_ACTIVITY: TagGroupKey = 'act';
export const TAG_GROUP_KEY_DATA_SHARING: TagGroupKey = 'ds';

const VALID_GROUP_KEYS = new Set<string>([TAG_GROUP_KEY_DAY_CODE, TAG_GROUP_KEY_ACTIVITY, TAG_GROUP_KEY_DATA_SHARING]);

/**
 * Encode a tag filter key with optional group disambiguation.
 * Returns "name~groupKey" when a groupKey is provided, plain name otherwise.
 */
export const buildTagFilterKey = (name: string, groupKey?: TagGroupKey): string => {
    if (groupKey) {
        return `${name}~${groupKey}`;
    }
    return name;
};

/**
 * Parse a tag filter key into its name and optional group key.
 * Returns { name, groupKey } where groupKey is null for plain names
 * or keys with unrecognised suffixes.
 */
export const parseTagFilterKey = (key: string): { name: string; groupKey: TagGroupKey | null } => {
    const tildeIdx = key.lastIndexOf('~');
    if (tildeIdx === -1) {
        return { name: key, groupKey: null };
    }
    const suffix = key.substring(tildeIdx + 1);
    if (VALID_GROUP_KEYS.has(suffix)) {
        return { name: key.substring(0, tildeIdx), groupKey: suffix as TagGroupKey };
    }
    return { name: key, groupKey: null };
};

/**
 * Strip the ~groupKey suffix from a tag filter key, returning just the name.
 * Only strips when the suffix is a recognised group key (e.g. "MD+1~dc" → "MD+1").
 * Leaves plain names and names with unrecognised suffixes unchanged,
 * so tildes in actual tag names are handled gracefully.
 */
export const stripTagGroupSuffix = (key: string): string => parseTagFilterKey(key).name;
